import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on, Action } from '@ngrx/store';

import * as UserDetailsActions from './user-details.actions';
import { UserDetailsEntity } from './user-details.models';

export const USER_DETAILS_FEATURE_KEY = 'userDetails';

export interface UserDetailsState extends EntityState<UserDetailsEntity> {
  selectedId?: string | number; // which UserDetails record has been selected
  loaded: boolean; // has the UserDetails list been loaded
  userImageStatus: boolean; // has the UserDetails list been loaded
  error?: string | null; // last known error (if any)
  profileData: any;
  userProfileOverview: any;
  organizationName: any;
  userPersonalInfo: [] | any;
  userEmailPreferencesStatus: any;
  userProfileImageData: any;
  organizationList: any;
  selectedOrg: any;
  organizationData: [] | null;
}

export interface UserDetailsPartialState {
  readonly [USER_DETAILS_FEATURE_KEY]: UserDetailsState;
}

export const userDetailsAdapter: EntityAdapter<UserDetailsEntity> =
  createEntityAdapter<UserDetailsEntity>();

export const initialUserDetailsState: UserDetailsState = userDetailsAdapter.getInitialState({
  // set initial required properties
  loaded: false,
  profileData: null,
  userProfileOverview: null,
  userPersonalInfo: [],
  userProfileImageData: null,
  userEmailPreferencesStatus: {},
  userImageStatus: false,
  organizationList: {},
  organizationData: [],
  organizationName: null,
  selectedOrg: '',
});

const reducer = createReducer(
  initialUserDetailsState,
  //reducer for loggedInUserProfile.
  on(UserDetailsActions.loggedInUserProfile, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(UserDetailsActions.loggedInUserProfileSuccess, (state, { profileData }) => {
    return { ...state, profileData: profileData, loaded: true };
  }),
  on(UserDetailsActions.loggedInUserProfileFailure, (state, { error }) => ({ ...state, error })),
  //reducer for userProfileOverview.
  on(UserDetailsActions.userProfileOverview, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(UserDetailsActions.userProfileOverviewSuccess, (state, { userProfileOverview }) => {
    return { ...state, userProfileOverview: userProfileOverview, loaded: true };
  }),
  on(UserDetailsActions.userProfileOverviewFailure, (state, { error }) => ({ ...state, error })),
  on(UserDetailsActions.clearUserProfileOverview, state => ({
    ...state,
    userProfileOverview: null,
  })),
  //reducer for saveUserPersonalData.
  on(UserDetailsActions.saveUserPersonalInfo, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(UserDetailsActions.saveUserPersonalInfoSuccess, (state, { userProfileOverview }) => {
    return { ...state, userProfileOverview: userProfileOverview, loaded: true };
  }),
  on(UserDetailsActions.saveUserPersonalInfoFailure, (state, { error }) => ({ ...state, error })),
  //reducer for getUserPersonalInfo.
  on(UserDetailsActions.getUserPersonalInfo, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(UserDetailsActions.getUserPersonalInfoSuccess, (state, { userPersonalInfo }) => {
    return { ...state, userPersonalInfo: userPersonalInfo, loaded: true };
  }),
  on(UserDetailsActions.getUserPersonalInfoFailure, (state, { error }) => ({ ...state, error })),
  //reducer for getUserProfileImage.
  on(UserDetailsActions.getUserProfileImage, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(UserDetailsActions.getUserProfileImageSuccess, (state, { userProfileImageData }) => {
    return { ...state, userProfileImageData: userProfileImageData, loaded: true };
  }),
  on(UserDetailsActions.getUserProfileImageFailure, (state, { error }) => ({ ...state, error })),
  //reducer for updateUserProfileImage.
  on(UserDetailsActions.updateUserProfileImage, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(
    UserDetailsActions.updateUserProfileImageSuccess,
    (state, { userProfileImageData, userImageStatus }) => {
      return {
        ...state,
        userImageStatus: userImageStatus,
        userProfileImageData: userProfileImageData,
        loaded: true,
      };
    }
  ),
  on(UserDetailsActions.updateUserProfileImageFailure, (state, { error }) => ({ ...state, error })),
  //reducer for getUserEmailPreferences.
  on(UserDetailsActions.getUserEmailPreferences, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(UserDetailsActions.getUserEmailPreferencesSuccess, (state, { userEmailPreferencesStatus }) => {
    return { ...state, userEmailPreferencesStatus: userEmailPreferencesStatus, loaded: true };
  }),
  on(UserDetailsActions.getUserEmailPreferencesFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  //reducer for UpdateUserEmailPreferencesStatus.
  on(UserDetailsActions.updateUserEmailPreferences, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(
    UserDetailsActions.updateUserEmailPreferencesSuccess,
    (state, { userEmailPreferencesStatus }) => {
      return { ...state, userEmailPreferencesStatus: userEmailPreferencesStatus, loaded: true };
    }
  ),
  on(UserDetailsActions.updateUserEmailPreferencesFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  //reducer for getOrganizationList.
  on(UserDetailsActions.getOrganization, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(UserDetailsActions.getOrganizationSuccess, (state, { organizationList, selectedOrg }) => {
    return { ...state, organizationList: organizationList, selectedOrg: selectedOrg, loaded: true };
  }),
  on(UserDetailsActions.getOrganizationFailure, (state, { error }) => ({ ...state, error })),
  //reducer for changeOrganizationList.
  on(UserDetailsActions.changeOrganization, (state, payload) => {
    return { ...state, payload: payload, loaded: false, error: null };
  }),
  on(UserDetailsActions.changeOrganizationSuccess, (state, { organizationData }) => {
    return { ...state, organizationData: organizationData, loaded: true };
  }),
  on(UserDetailsActions.changeOrganizationFailure, (state, { error }) => ({ ...state, error })),

  on(UserDetailsActions.clearStateOfUserDetaills, state => ({ ...state, userImageStatus: false })),
  on(UserDetailsActions.regenerateAccessTokenSuccess, (state, { organizationName }) => {
    return { ...state, organizationName: organizationName, loaded: true };
  }),

  // To clear user image data
  on(UserDetailsActions.clearUserProfileImage, state => ({ ...state, userProfileImageData: '' }))
);

export function userDetailsReducer(state: UserDetailsState | undefined, action: Action) {
  return reducer(state, action);
}
